import type {
  ImportColumn,
  ImportErrorCode,
  ImportRowErrorDto,
  ImportSubmitErrorCode,
} from '../api/created/importFile.ts';
import { getContactTracingImportMessage } from './contactTracingImportCode.ts';

// The backend counts Excel rows, where row 1 is the header. The review page shows the first contact as row 1.
export function toDisplayRowNumber(rowNumber: number) {
  return rowNumber - 1;
}

// An upload can be used for one day, then preview (HTTP 410) and submit (FILE.EXPIRED) reject it.
export const UPLOAD_EXPIRED_MESSAGE = 'Die hochgeladene Datei ist abgelaufen. Bitte die Datei erneut hochladen.';
// An upload can be imported once, then preview (HTTP 409) and submit (FILE.ALREADY_SUBMITTED) reject it.
export const UPLOAD_ALREADY_IMPORTED_MESSAGE = 'Diese Datei wurde bereits importiert.';

export function getImportSubmitMessage(code: ImportSubmitErrorCode | undefined): string {
  switch (code) {
    // The same codes as the upload, with the same messages.
    case 'CASE_ID.REQUIRED':
    case 'CASE_ID.NOT_FOUND':
    case 'CASE_ID.NOT_INDEX_CASE':
      return getContactTracingImportMessage(code);
    case 'IMPORT.REQUEST_INVALID':
      return 'Die Anfrage ist ungültig. Bitte die Seite neu laden und erneut versuchen.';
    case 'CASE_ID.BOTH_GIVEN':
      return 'Es darf nur ein Fall angegeben werden';
    case 'CASE_ID.CONTACT_TRACING_DISABLED':
      return 'Für diesen Indexpatienten ist das Contact Tracing nicht aktiviert';
    case 'SCREENING_CASE_ID.NOT_FOUND':
      return 'Das Screening wurde nicht gefunden';
    case 'FILE.NOT_FOUND':
      return 'Die hochgeladene Datei wurde nicht gefunden. Bitte die Datei erneut hochladen.';
    case 'FILE.ALREADY_SUBMITTED':
      return UPLOAD_ALREADY_IMPORTED_MESSAGE;
    case 'FILE.EXPIRED':
      return UPLOAD_EXPIRED_MESSAGE;
    case 'FILE.CASE_MISMATCH':
      return 'Die Datei wurde für einen anderen Fall hochgeladen';
    case 'IMPORT.NO_ROWS':
      return 'Es wurde keine Zeile zum Importieren ausgewählt';
    case 'IMPORT.ROWS_INVALID':
      return 'Einige Zeilen können nicht importiert werden. Bitte die markierten Zeilen prüfen.';
    case 'IMPORT.WRITE_REJECTED':
      return 'Die Daten wurden in der Zwischenzeit geändert. Bitte die Seite neu laden und erneut versuchen.';
    case 'IMPORT.INTERNAL_ERROR':
      return 'Beim Importieren der Kontakte ist ein unerwarteter Fehler aufgetreten';
    default:
      return 'Beim Importieren der Kontakte ist ein Fehler aufgetreten';
  }
}

// alreadyAssignedMessage names the case the person is already part of, for example a contact or a screening.
export function getImportRowErrorMessage(
  rowError: ImportRowErrorDto,
  alreadyAssignedMessage = 'Die Person ist bereits als Kontakt erfasst',
): string {
  const otherRow = rowError.otherRowNumber !== undefined ? toDisplayRowNumber(rowError.otherRowNumber) : '?';
  switch (rowError.code) {
    case 'IMPORT_ROW.ROW_NUMBER_REPEATED':
      return 'Die Zeile wurde mehrfach übermittelt';
    case 'IMPORT_ROW.CELL_INVALID':
      return `${getColumnLabel(rowError.column)}: ${getCellErrorText(rowError.cellCode)}`;
    case 'IMPORT_ROW.PERSON_ID_REQUIRED':
      return 'Es wurde keine Stammdaten-Person ausgewählt';
    case 'IMPORT_ROW.PERSON_NOT_FOUND':
      return 'Die ausgewählte Stammdaten-Person existiert nicht mehr';
    case 'IMPORT_ROW.PERSON_ALREADY_ASSIGNED':
      return alreadyAssignedMessage;
    case 'IMPORT_ROW.PERSON_LINKED_TWICE':
      return `Die gleiche Person wird bereits in Zeile ${otherRow} verknüpft`;
    case 'IMPORT_ROW.NEW_PERSON_REPEATED':
      return `Die gleiche neue Person wird bereits in Zeile ${otherRow} erstellt`;
    default:
      return 'Die Zeile kann nicht importiert werden';
  }
}

function getColumnLabel(column: ImportColumn | undefined): string {
  switch (column) {
    case 'GENDER':
      return 'Geschlecht';
    case 'FIRST_NAME':
      return 'Vorname';
    case 'LAST_NAME':
      return 'Nachname';
    case 'BIRTH_DATE':
      return 'Geburtsdatum';
    case 'STREET':
      return 'Strasse';
    case 'POSTAL_CODE':
      return 'PLZ';
    case 'CITY':
      return 'Ort';
    case 'NATIONALITY':
      return 'Nationalität';
    case 'ASYLUM_NUMBER':
      return 'N-Nummer';
    case 'NOTE':
      return 'Bemerkungen';
    case 'LAST_CONTACT_DATE':
      return 'Datum letzter Kontakt';
    default:
      return 'Zelle';
  }
}

function getCellErrorText(cellCode: ImportErrorCode | undefined): string {
  switch (cellCode) {
    case 'REQUIRED':
      return 'fehlt';
    case 'INVALID_DATE':
      return 'ungültiges Datum';
    case 'DATE_IN_FUTURE':
      return 'Datum liegt in der Zukunft';
    case 'INVALID_POSTAL_CODE':
      return 'ungültige PLZ';
    case 'UNKNOWN_COUNTRY':
      return 'unbekanntes Land';
    case 'UNKNOWN_GENDER':
      return 'unbekanntes Geschlecht';
    default:
      return 'ungültiger Wert';
  }
}
